import React, { useEffect, useState } from "react";
import Popup from "../../../components/common/model/Popup";
import { DialogContent, DialogContentText, Grid } from "@mui/material";
import useStyles from "../../../components/common/model/styles";
import CustomButton from "../../../components/common/button/CustomButton";
import { sendSmsAllFields } from "./templateUtil";
import FormField from "../../../components/common/FormField";
import Notification from "../../../components/common/notification/Notification";
import Validation from "../../../utils/Validation";
import { useDispatch } from "react-redux";
import {
  sendSmsTemplateAction,
  setSendSmsTemplateAction,
} from "../../../store/reducers/templateSlice";
import { useSelector } from "react-redux";

interface PreviewTemplateProps {
  onHide: () => void;
  open: boolean;
  selectedSms: any;
  title?: string;
}

const PreviewTemplate: React.FC<PreviewTemplateProps> = ({
  onHide,
  open,
  selectedSms,
  title,
}) => {
  const classes = useStyles();

  const { isSmsTemplateSendSuccess } = useSelector(
    (state: any) => state.template
  );
  const { leadDetails } = useSelector((state: any) => state.lead);
  const { editClientOriginalData } = useSelector(
    (state: any) => state.adminClients
  );
  const dispatch = useDispatch();

  const [countryCode, setCountryCode] = useState<string>(
    leadDetails?.lead?.customer_country_code ?? "+44"
  ); // State for country code

  const [formData, setFormData] = useState<{ [key: string]: string }>({
    to: leadDetails?.lead?.customer_phone
      ? leadDetails?.lead?.customer_phone
      : "",
    message: "",
  });

  // ---------- placeholder mapping ----------
  // map placeholder name (inside {{ }}) to the data path (rootName.path...)
  // e.g. "username": "lead.customer_name"
  const placeholderMapping: Record<string, string> = {
    username: "lead.customer_name",
    email: "editClientOriginalData.email",
    clientName: "editClientOriginalData.contact_name",
    clientAddress: "editClientOriginalData.address",
    clientWebsite: "editClientOriginalData.website",
    customerName: "lead.customer_name",
    customerEmail: "lead.customer_email",
    customerPhone: "lead.customer_phone",
    clientId: "lead.client_id",
    createdAt: "lead.created_at",
    collectionVolume: "lead.collection_volume",
    collectionAddress: "lead.collection_address",
    deliveryAddress: "lead.delivery_address",
    materialsTable: "lead.materials_table",
    materialIncluded: "lead.material_included",
    servicesTable: "lead.services_table",
    signature: "lead.signature",
    CName: "lead.customer_name",
    JobRef: "lead.generated_id",
    CompanyName: "editClientOriginalData.company_name",
    CMobile1: "lead.customer_phone",
    JobAddress: "lead.collection_address[0].address",
    CompanyNumber: "editClientOriginalData.phone_number",
    Email: "lead.customer_email",
  };

  // ---------- Helpers ----------
  // Convert bracket indexes to dot notation and walk the object safely
  const getNestedValue = (obj: any, path: string): any => {
    if (!obj || !path) return "";
    // convert a[0].b -> a.0.b then split
    const normalized = path.replace(/\[(\d+)\]/g, ".$1");
    const parts = normalized.split(".").filter(Boolean);
    let cur: any = obj;
    for (const p of parts) {
      if (cur == null) return "";
      cur = cur[p];
    }
    // If array, join; otherwise return primitive or stringified object
    if (Array.isArray(cur)) return cur.join(", ");
    if (cur === null || cur === undefined) return "";
    if (typeof cur === "object") return JSON.stringify(cur);
    return String(cur);
  };

  // sources to look for values (root names referenced in mapping)
  const sources: Record<string, any> = {
    lead: leadDetails?.lead ?? {},
    editClientOriginalData: editClientOriginalData ?? {},
  };

  // Replace placeholders in a message body
  const replacePlaceholders = (message: string): string => {
    if (!message) return "";

    return message.replace(/{{\s*([^}]+)\s*}}/g, (_, rawKey: string) => {
      const key = rawKey.trim();

      // 1) If we have an explicit mapping, use it
      const mappedPath = placeholderMapping[key];
      if (mappedPath) {
        const [rootName, ...rest] = mappedPath.split(".");
        const innerPath = rest.join(".");
        const rootObj = sources[rootName];
        if (rootObj) {
          const val = getNestedValue(rootObj, innerPath);
          return val ?? "";
        }
        return "";
      }

      // 2) If no mapping, try searching each source for the direct key
      for (const rootName of Object.keys(sources)) {
        const val = getNestedValue(sources[rootName], key);
        if (val !== "" && val !== undefined) return val;
      }

      // 3) Not found — return empty string (change to `{{key}}` to keep tag)
      return "";
    });
  };

  // When selectedSms or data sources change, fill the message with replaced placeholders
  useEffect(() => {
    if (selectedSms) {
      const raw = selectedSms?.body ?? "";
      const replaced = replacePlaceholders(raw);
      setFormData((prev) => ({
        ...prev,
        message: replaced,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSms, leadDetails, editClientOriginalData]);

  const [isActionCalled, setIsActionCalled] = useState(false);

  const [
    error,
    checkValidator,
    resetValidator,
    resetAllValidator,
    isError,
  ]: any = Validation({
    to: formData?.to,
    message: formData?.message,
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    checkValidator();
    setIsActionCalled(true);
  };

  const onChangeAction = (event: any) => {
    const { id, value } = event.target;

    setFormData((prevState: any) => ({
      ...prevState,
      [id]: value,
    }));

    resetValidator(event.target.id);
  };

  const onBlurAction = (e: any) => {
    checkValidator(e.target.id);
  };

  useEffect(() => {
    if (isActionCalled) {
      if (!isError() && error !== null && isActionCalled) {
        const { ...payload } = formData;

        // attach the country code to 'to' if present
        if (formData?.to) {
          payload.to = `${countryCode}${formData?.to}`;
        }

        // if you want to ensure placeholders are resolved at send-time as well,
        // run replacePlaceholders on payload.message again.
        payload.message = replacePlaceholders(payload.message || "");
        // NEW: attach leadId so the backend knows which lead this SMS belongs to
        payload.leadId = leadDetails?.lead?.generated_id;
        dispatch(sendSmsTemplateAction(payload));
      }
      setIsActionCalled(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActionCalled]);

  useEffect(() => {
    if (isSmsTemplateSendSuccess) {
      dispatch(setSendSmsTemplateAction(false));
      onHide();
    }
  }, [isSmsTemplateSendSuccess, dispatch, onHide]);

  return (
    <Popup
      title={selectedSms?.template_name}
      fullWidth
      maxWidth="md"
      open={open}
      onHide={onHide}
      popupBg="#fff"
      footer={
        <>
          <CustomButton
            style={{ padding: "10px 30px", height: "36px" }}
            title="Send"
            onClick={handleSubmit}
          />
        </>
      }
    >
      <DialogContent sx={{ padding: "0px 10px" }}>
        <DialogContentText>
          <>
            <Notification />
            <Grid
              container
              direction="row"
              spacing={2}
              sx={{
                mt: 1,
                backgroundColor: "#E5F4F1",
                padding: "10px 16px 10px 0px",
                borderRadius: "5px",
                border: "1px solid #CFCFCF",
              }}
            >
              {sendSmsAllFields?.map((field, i) => (
                <FormField
                  key={field.id}
                  onPaste={(e: any) => {
                    const pasteText = e.clipboardData.getData("text");
                    const maxLength = field?.inputProps?.maxLength || 1600;

                    if ((e.target.value + pasteText).length > maxLength) {
                      e.preventDefault();
                    }
                  }}
                  onKeyDown={(event: any) => {
                    if (
                      [8, 9, 37, 38, 39, 40, 18, 17].includes(event.keyCode) ||
                      event.ctrlKey
                    )
                      return event;
                    if (
                      field?.patternAllowed &&
                      !field?.patternAllowed.test(event.key)
                    ) {
                      event.preventDefault();
                    } else if (
                      field?.patternAllowed &&
                      !field?.patternAllowed.test(
                        event.target.value + event.key
                      )
                    ) {
                      event.preventDefault();
                    }
                  }}
                  {...{
                    ...field,
                    value: formData ? formData[field.id] : "",
                    variant: "outlined",
                    inSingleGridForm: true,
                    onCountryCodeChange:
                      field.type === "contact"
                        ? (code: string) => setCountryCode(code)
                        : undefined,
                    onChange: onChangeAction,
                    onBlur: onBlurAction,
                    isNonMarginTop: true,
                    countryCode,
                    maxLength: field?.inputProps?.maxLength,
                    InputProps: {
                      ...(field?.inputProps && field?.inputProps),
                      sx:
                        field.type === "text" || field.type === "password"
                          ? { height: "36px" }
                          : { padding: "10px 4px", height: "36px" },
                    },
                    error: error && error[field.id],
                  }}
                />
              ))}
            </Grid>
          </>
        </DialogContentText>
      </DialogContent>
    </Popup>
  );
};

export default PreviewTemplate;
